'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useMotionValue, useTransform, useReducedMotion, animate, PanInfo } from 'framer-motion';

/**
 * SwipeDeck — интерактивная колода, tinder-style.
 *
 * Архитектура:
 *  - Стек: items[1..2] рендерятся как статические карточки за топом (z-index ниже).
 *  - TopCard — ОТДЕЛЬНЫЙ компонент с локальными motion x/y. Ключ = item.id.
 *    При смене топа ключ меняется → unmount/mount → свежие motion values.
 *    Это убирает баг "карточка прискакивает" (раньше x/y были общие на всех топов).
 *  - swipeApiRef хранит ссылку на свежую функцию swipeOff текущей TopCard.
 *  - autoplay интервал стопится синхронно через interactedRef + clearInterval
 *    при первом действии юзера.
 *  - Toast рендерится без AnimatePresence — при смене id просто перемонтируется
 *    через React-key, что моментально (без mode="wait" задержки).
 */

type SwipeItem = { id: string; category: string; source: string; title: string; body?: string; bg: string };
type Direction = 'left' | 'right' | 'up';

const INITIAL_ITEMS: SwipeItem[] = [
  { id: 'a', category: '🎬', source: 'Маша',          title: 'Форест Гамп',           body: '1994, реж. Земекис',     bg: '#e57b7b' },
  { id: 'b', category: '🍳', source: 'мама',          title: 'Блинчики на кефире',    body: 'кефир, мука, яйцо',       bg: '#d49a3f' },
  { id: 'c', category: '🎮', source: 'Антон',         title: 'Hollow Knight',         body: 'Steam · 200₽',            bg: '#7b8be5' },
  { id: 'd', category: '📚', source: 'Книги до 30',   title: '«1984», Оруэлл',        body: '1949 · антиутопия',       bg: '#5a7558' },
  { id: 'e', category: '📍', source: 'Лена',          title: 'Кофейня Прокуратор',    body: 'Покровка, 17',            bg: '#6b9e7f' },
  { id: 'f', category: '🎵', source: 'Spotify',       title: 'Плейлист «весна 2026»', body: '24 трека',                bg: '#4a6e8e' },
];

const SWIPE_DISTANCE = 600;
const DRAG_THRESHOLD = 100;
const AUTO_INTERVAL_MS = 2600;
const TOAST_MS = 2400;
const SWIPE_ANIM_MS = 350;

const TOAST: Record<Direction, { text: string; dot: string; fg: string }> = {
  right: { text: 'Закреплено — на самом верху ленты.',   dot: '#b7102a', fg: '#b7102a' },
  up:    { text: 'Отправлено в Лучшее.',                  dot: '#5a7558', fg: '#5a7558' },
  left:  { text: 'Будет удалено через 10 дней.',         dot: '#7a8a79', fg: '#7a8a79' },
};

export default function SwipeDeck() {
  const reduced = useReducedMotion() ?? false;
  const [items, setItems] = useState(INITIAL_ITEMS);
  const [userInteracted, setUserInteracted] = useState(false);
  const [toast, setToast] = useState<{ id: number; dir: Direction } | null>(null);

  const animatingRef = useRef(false);
  const interactedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastIdRef = useRef(0);
  const swipeApiRef = useRef<((dir: Direction) => Promise<void>) | null>(null);

  const showToast = useCallback((dir: Direction) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastIdRef.current += 1;
    setToast({ id: toastIdRef.current, dir });
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, TOAST_MS);
  }, []);

  const stopAutoplay = useCallback(() => {
    interactedRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setUserInteracted(true);
  }, []);

  const triggerSwipe = useCallback(
    (dir: Direction, fromUser: boolean) => {
      if (animatingRef.current) return;
      const api = swipeApiRef.current;
      if (!api) return;
      animatingRef.current = true;

      if (fromUser) {
        stopAutoplay();
        showToast(dir);
      }

      api(dir).then(() => {
        setItems((prev) => [...prev.slice(1), prev[0]]);
        // Снимаем lock на следующем кадре, после того как React успел смонтировать
        // новый TopCard. Иначе автоплей-тик может выстрелить с пустым swipeApiRef.
        requestAnimationFrame(() => {
          animatingRef.current = false;
        });
      });
    },
    [stopAutoplay, showToast],
  );

  useEffect(() => {
    if (reduced) return;
    intervalRef.current = setInterval(() => {
      if (interactedRef.current || animatingRef.current) return;
      const dirs: Direction[] = ['right', 'left', 'up'];
      triggerSwipe(dirs[Math.floor(Math.random() * dirs.length)], false);
    }, AUTO_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [reduced, triggerSwipe]);

  useEffect(
    () => () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    },
    [],
  );

  const top = items[0];
  const middle = items[1];
  const back = items[2];

  return (
    <div className="relative w-full max-w-[320px] mx-auto">
      <div className="relative h-[400px] overflow-hidden rounded-[1.5rem]">
        {/* Background: дальняя карточка */}
        {back && (
          <motion.div
            key={`back-${back.id}`}
            className="absolute inset-0 pointer-events-none"
            style={{ zIndex: 8 }}
            initial={{ scale: 0.9, y: 16, opacity: 0.6 }}
            animate={{ scale: 0.9, y: 16, opacity: 0.6 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          >
            <Card item={back} />
          </motion.div>
        )}
        {/* Middle: средняя карточка */}
        {middle && (
          <motion.div
            key={`mid-${middle.id}`}
            className="absolute inset-0 pointer-events-none"
            style={{ zIndex: 9 }}
            initial={{ scale: 0.95, y: 8, opacity: 1 }}
            animate={{ scale: 0.95, y: 8, opacity: 1 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          >
            <Card item={middle} />
          </motion.div>
        )}
        {/* Top: интерактивная карточка с локальными motion values */}
        {top && (
          <TopCard
            key={top.id}
            item={top}
            onSwipeRequest={(dir) => triggerSwipe(dir, true)}
            onApi={(fn) => {
              swipeApiRef.current = fn;
            }}
            onDragStart={stopAutoplay}
          />
        )}
      </div>

      {/* Action buttons: [🗑][❤][📌] — порядок и семантика совпадают с
          боевым каталогом (см. EntryActions). */}
      <div className="flex items-center justify-center gap-3 mt-6">
        <ActionButton ariaLabel="Удалить" onClick={() => triggerSwipe('left', true)} colorClass="text-[#7a8a79] hover:border-[#7a8a79]">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
          </svg>
        </ActionButton>
        <ActionButton ariaLabel="В Лучшее" onClick={() => triggerSwipe('up', true)} colorClass="text-[#5a7558] hover:border-[#5a7558]">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </ActionButton>
        <ActionButton ariaLabel="Закрепить" onClick={() => triggerSwipe('right', true)} colorClass="text-[#b7102a] hover:border-[#b7102a]">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="17" x2="12" y2="22" />
            <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
          </svg>
        </ActionButton>
      </div>

      {/* Feedback area */}
      <div className="mt-4 min-h-[36px] flex items-center justify-center relative">
        {toast ? (
          <motion.div
            key={`t-${toast.id}`}
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-[#dce5d9]"
            style={{ boxShadow: '0 6px 16px -8px rgba(20,40,20,0.18)' }}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: TOAST[toast.dir].dot }} />
            <span className="font-body text-[13px]" style={{ color: TOAST[toast.dir].fg }}>
              {TOAST[toast.dir].text}
            </span>
          </motion.div>
        ) : !userInteracted && !reduced ? (
          <span className="font-mono text-[10px] text-[#7a8a79] uppercase tracking-widest text-center">
            потяни карточку, чтобы попробовать
          </span>
        ) : null}
      </div>
    </div>
  );
}

/* ────────────────────── TopCard ────────────────────── */

function TopCard({
  item,
  onSwipeRequest,
  onApi,
  onDragStart,
}: {
  item: SwipeItem;
  onSwipeRequest: (dir: Direction) => void;
  onApi: (fn: ((dir: Direction) => Promise<void>) | null) => void;
  onDragStart: () => void;
}) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const labelLeftOpacity = useTransform(x, [-180, -50, 0], [1, 0.4, 0]);
  const labelRightOpacity = useTransform(x, [0, 50, 180], [0, 0.4, 1]);
  const labelUpOpacity = useTransform(y, [-180, -50, 0], [1, 0.4, 0]);

  // Программный свайп (для action-кнопок). Anim возвращает Promise, родитель
  // ждёт завершения перед сменой items.
  useEffect(() => {
    const swipeOff = (dir: Direction) => {
      const dx = dir === 'right' ? SWIPE_DISTANCE : dir === 'left' ? -SWIPE_DISTANCE : 0;
      const dy = dir === 'up' ? -SWIPE_DISTANCE : 0;
      const a1 = animate(x, dx, { duration: SWIPE_ANIM_MS / 1000, ease: 'easeOut' });
      const a2 = animate(y, dy, { duration: SWIPE_ANIM_MS / 1000, ease: 'easeOut' });
      return Promise.all([a1, a2]).then(() => undefined);
    };
    onApi(swipeOff);
    return () => onApi(null);
  }, [x, y, onApi]);

  const handleDragEnd = (_e: unknown, info: PanInfo) => {
    const dx = info.offset.x;
    const dy = info.offset.y;
    if (dx > DRAG_THRESHOLD) onSwipeRequest('right');
    else if (dx < -DRAG_THRESHOLD) onSwipeRequest('left');
    else if (dy < -DRAG_THRESHOLD) onSwipeRequest('up');
    else {
      animate(x, 0, { duration: 0.25, ease: 'easeOut' });
      animate(y, 0, { duration: 0.25, ease: 'easeOut' });
    }
  };

  return (
    <motion.div
      className="absolute inset-0 will-change-transform"
      style={{ zIndex: 10, x, y }}
      drag
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={1}
      onDragStart={onDragStart}
      onDragEnd={handleDragEnd}
      whileDrag={{ cursor: 'grabbing' }}
    >
      <Card item={item} draggable />
      <motion.div style={{ opacity: labelLeftOpacity }} className="absolute top-6 left-6 px-3 py-1.5 rounded-lg bg-[#7a8a79] text-white font-display font-bold text-sm uppercase tracking-wider rotate-[-12deg] pointer-events-none border-2 border-white">
        Удалить
      </motion.div>
      <motion.div style={{ opacity: labelRightOpacity }} className="absolute top-6 right-6 px-3 py-1.5 rounded-lg bg-[#b7102a] text-white font-display font-bold text-sm uppercase tracking-wider rotate-[12deg] pointer-events-none border-2 border-white">
        Закрепить
      </motion.div>
      <motion.div style={{ opacity: labelUpOpacity }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg bg-[#5a7558] text-white font-display font-bold text-sm uppercase tracking-wider pointer-events-none border-2 border-white">
        В Лучшее
      </motion.div>
    </motion.div>
  );
}

/* ────────────────────── Helpers ────────────────────── */

function ActionButton({
  children,
  onClick,
  colorClass,
  ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  colorClass: string;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={`flex items-center justify-center rounded-full border-2 border-[#dce5d9] bg-white w-12 h-12 transition-all active:scale-90 ${colorClass}`}
      style={{ boxShadow: '0 8px 20px -8px rgba(20,40,20,0.15)' }}
    >
      {children}
    </button>
  );
}

function Card({ item, draggable = false }: { item: SwipeItem; draggable?: boolean }) {
  return (
    <div
      className={`absolute inset-0 rounded-[1.5rem] overflow-hidden bg-white border border-[#dce5d9] flex flex-col ${
        draggable ? 'cursor-grab' : ''
      }`}
      style={{ boxShadow: '0 24px 48px -16px rgba(20,40,20,0.2)' }}
    >
      <div
        className="flex-1 flex items-center justify-center text-7xl"
        style={{ background: `linear-gradient(135deg, ${item.bg}40 0%, ${item.bg}10 100%)` }}
      >
        <span className="drop-shadow-md">{item.category}</span>
      </div>
      <div className="px-5 py-4 border-t border-[#e7f0e5]">
        <div className="font-body text-[#7a8a79] text-[11px] mb-1">{item.source}</div>
        <div className="font-display font-bold text-[#161d16] text-base leading-tight mb-1">{item.title}</div>
        {item.body && <div className="font-body text-[#4a5a49] text-xs">{item.body}</div>}
      </div>
    </div>
  );
}
